import axios from 'axios';

// API Configuration - relative '/api' works on localhost and production VPS IP/domain
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const API_TIMEOUT = 10000; // 10 seconds

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('helpbook_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (process.env.REACT_APP_DEBUG === 'true') {
      console.log('🔄 API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data
      });
    }

    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.log('✅ API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data
      });
    }

    return response;
  },
  (error) => {
    console.error('❌ API Error:', error);

    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          localStorage.removeItem('helpbook_token');
          localStorage.removeItem('helpbook_user');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          break;

        case 403:
          console.warn('🚫 Access forbidden');
          break;

        case 404:
          console.warn('🔍 Resource not found');
          break;

        case 429:
          console.warn('⏰ Rate limit exceeded');
          break;

        case 500:
          console.error('🔥 Server error');
          break;

        default:
          console.error(`❌ HTTP ${status}:`, data?.error || error.message);
      }

      return Promise.reject({
        status,
        message: data?.error || error.message,
        data
      });
    } else if (error.request) {
      console.error('🌐 Network Error:', error.request);
      return Promise.reject({
        status: 0,
        message: 'Netzwerkfehler. Bitte überprüfe deine Internetverbindung.',
        data: null
      });
    } else {
      console.error('❌ Unknown Error:', error.message);
      return Promise.reject({
        status: -1,
        message: error.message,
        data: null
      });
    }
  }
);

// API Helper Functions
export const apiHelpers = {
  get: async (url, config = {}) => {
    try {
      const response = await api.get(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await api.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await api.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  delete: async (url, config = {}) => {
    try {
      const response = await api.delete(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  upload: async (url, formData, onProgress = null) => {
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onProgress ? (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        } : undefined,
      };

      const response = await api.post(url, formData, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Auth API
export const authAPI = {
  login: (contact, password) =>
    api.post('/auth/login', { contact, password }),

  verifyMFA: (email, code, type = 'totp') =>
    api.post('/auth/verify-mfa', { email, code, type }),

  setupTotp: () =>
    api.post('/auth/totp/setup'),

  verifyTotpSetup: (code) =>
    api.post('/auth/totp/verify-setup', { code }),

  disableTotp: () =>
    api.post('/auth/totp/disable'),

  requestTotpFallbackEmail: (email) =>
    api.post('/auth/totp/fallback-email', { email }),

  register: (userData) =>
    api.post('/auth/register', userData),

  verifyRegistration: (email, code) =>
    api.post('/auth/verify-registration', { email, code }),

  socialLogin: (provider, socialId, email, firstName, lastName, avatar, isOAuthVerified = false) =>
    api.post('/auth/social-login', { provider, socialId, email, firstName, lastName, avatar, isOAuthVerified }),

  resendCode: (email, type) =>
    api.post('/auth/resend-code', { email, type }),

  toggleMFA: (mfaEnabled) =>
    api.put('/auth/toggle-mfa', { mfaEnabled }),

  requestAccountDelete: () =>
    api.post('/auth/request-account-delete'),

  confirmAccountDelete: (code) =>
    api.post('/auth/confirm-account-delete', { code }),

  passwordResetRequest: (contact) =>
    api.post('/auth/password-reset-request', { contact }),

  passwordResetVerify: (contact, resetCode, newPassword) =>
    api.post('/auth/password-reset-verify', { contact, resetCode, newPassword }),

  getMe: () =>
    api.get('/auth/me'),

  updateProfile: (profileData) =>
    api.put('/auth/profile', profileData),

  changePassword: (currentPassword, newPassword) =>
    api.put('/auth/change-password', { currentPassword, newPassword }),

  deleteAccount: () =>
    api.delete('/auth/profile'),
};

// Community API
export const communityAPI = {
  getPosts: (category = 'alle') =>
    api.get(`/community/posts?category=${category}`),

  createPost: (postData) =>
    api.post('/community/posts', postData),

  likePost: (postId) =>
    api.post(`/community/posts/${postId}/like`, {}),

  getComments: (postId) =>
    api.get(`/community/posts/${postId}/comments`),

  createComment: (postId, content) =>
    api.post(`/community/posts/${postId}/comments`, { content }),

  deletePost: (postId) =>
    api.delete(`/community/posts/${postId}`),
};

// Marketplace API
export const marketplaceAPI = {
  getItems: (category = 'alle', type = 'alle') =>
    api.get(`/marketplace/items?category=${category}&type=${type}`),

  createItem: (itemData) =>
    api.post('/marketplace/items', itemData),

  favoriteItem: (itemId) =>
    api.post(`/marketplace/items/${itemId}/favorite`, {}),

  deleteItem: (itemId) =>
    api.delete(`/marketplace/items/${itemId}`),

  updateItemStatus: (itemId, status) =>
    api.put(`/marketplace/items/${itemId}/status`, { status }),
};

// Friends API
export const friendsAPI = {
  getFriends: () =>
    api.get('/friends'),

  getUserProfile: (userId) =>
    api.get(`/friends/${userId}`),

  getFriendRequests: () =>
    api.get('/friends/requests'),

  sendFriendRequest: (userId) =>
    api.post('/friends/request', { userId }),

  acceptFriendRequest: (requestId) =>
    api.post(`/friends/request/${requestId}/accept`),

  declineFriendRequest: (requestId) =>
    api.post(`/friends/request/${requestId}/decline`),

  removeFriend: (friendId) =>
    api.delete(`/friends/${friendId}`),

  searchUsers: (query) =>
    api.get(`/users/search?q=${query}`),
};

// Chat API
export const chatAPI = {
  getConversations: () =>
    api.get('/chat/conversations'),

  getMessages: (partnerId) =>
    api.get(`/chat/messages/${partnerId}`),

  sendMessage: (receiverId, message, mediaUrl = null, mediaType = null) =>
    api.post('/chat/send', typeof receiverId === 'object' ? receiverId : { receiverId, message, mediaUrl, mediaType }),

  startChat: (userId) =>
    api.post('/chat/start', { userId }),

  markAsRead: (partnerId) =>
    api.post(`/chat/read/${partnerId}`),
};

// Reviews API
export const reviewsAPI = {
  getUserReviews: (userId) =>
    api.get(`/reviews/user/${userId}`),

  createReview: (reviewData) =>
    api.post('/reviews', reviewData),
};

// Reports API
export const reportsAPI = {
  submitReport: (reportData) =>
    api.post('/reports', reportData),
};

// Notifications API
export const notificationsAPI = {
  getNotifications: () =>
    api.get('/notifications'),

  markAsRead: (id) =>
    api.post(`/notifications/${id}/read`),
};

// Feedback API
export const feedbackAPI = {
  submitFeedback: (feedbackData) =>
    api.post('/feedback', feedbackData),
};

// Blog API
export const blogAPI = {
  getArticles: () => api.get('/blog'),
  createArticle: (articleData) => api.post('/blog', articleData),
};

// Forum API
export const forumAPI = {
  getThreads: (category = 'alle') => api.get(`/forum?category=${category}`),
  getThread: (id) => api.get(`/forum/${id}`),
  createThread: (threadData) => api.post('/forum', threadData),
  createReply: (threadId, data) =>
    api.post(`/forum/${threadId}/reply`, typeof data === 'string' ? { content: data } : data),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (params = {}) => api.get('/admin/users', { params }),
};

export default api;
